import { useStaticQuery, graphql } from "gatsby"

import { linkIsNotNull } from "@utils/storyblok"
import { useMemo } from "react"

const INTERNAL_ARTICLE_PATH = /settings\/knowledge-hub-articles\/internal\//

type RawArticleNode = {
  uuid: string
  full_slug: string
  published_at: string | null
  created_at: string | null
  content: string
}

type KnowledgeHubArticlesQuery = {
  allStoryblokEntry: {
    nodes: RawArticleNode[]
  }
}

const isRenderableArticle = (
  value: unknown
): value is Storyblok.KnowledgeHubArticle =>
  typeof value === "object" &&
  value !== null &&
  "title" in value &&
  typeof value.title === "string" &&
  value.title.trim().length > 0 &&
  "image" in value &&
  typeof value.image === "object" &&
  value.image !== null &&
  "filename" in value.image &&
  typeof value.image.filename === "string" &&
  value.image.filename.length > 0

/**
 * Storyblok leaves published_at empty until a story has been published at least
 * once, so unpublished drafts fall back to created_at to keep the order stable.
 */
const getArticleTimestamp = (article: Storyblok.ArticleListItem) => {
  const articleDate = article.publishedAt || article.createdAt

  return articleDate ? new Date(articleDate).getTime() : 0
}

export const useKnowledgeHubArticles = (
  useInternalItems = false
): Storyblok.ArticleListItem[] => {
  const data = useStaticQuery<KnowledgeHubArticlesQuery>(graphql`
    query KnowledgeHubArticles {
      allStoryblokEntry(
        filter: {
          full_slug: { regex: "/settings/knowledge-hub-articles/" }
          field_component: { eq: "knowledgeHubArticle" }
        }
      ) {
        nodes {
          uuid
          full_slug
          published_at
          created_at
          content
        }
      }
    }
  `)

  return useMemo(() => {
    const articles = data.allStoryblokEntry.nodes.flatMap((node) => {
      let parsedContent: unknown

      try {
        parsedContent = JSON.parse(node.content)
      } catch {
        return []
      }

      const isInternal = INTERNAL_ARTICLE_PATH.test(node.full_slug)

      if (
        !isRenderableArticle(parsedContent) ||
        !linkIsNotNull(parsedContent.articleLink)
      ) {
        return []
      }

      if (isInternal !== useInternalItems) {
        return []
      }

      return [
        {
          uuid: node.uuid,
          fullSlug: node.full_slug,
          publishedAt: node.published_at,
          createdAt: node.created_at,
          content: parsedContent,
        },
      ]
    })

    return articles.sort(
      (article, comparedArticle) =>
        getArticleTimestamp(comparedArticle) - getArticleTimestamp(article)
    )
  }, [data, useInternalItems])
}
